import { api } from './api';

const USE_COOKIES = import.meta.env.VITE_ADMIN_AUTH_COOKIES === 'true';

// ── Types ──────────────────────────────────────────────────

export type AdminRole = 'superadmin' | 'moderator';

export interface AdminSession {
  userId: string;
  email: string;
  fullName: string;
  role: AdminRole;
  twoFactorEnabled: boolean;
  // Legacy Bearer-only fields. In cookie mode they stay undefined (tokens live server-side).
  token?: string;
  refreshToken?: string;
  tokenExpiresAt?: number;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  /** True when the account has 2FA enabled and needs verification */
  requires2FA: boolean;
  /** True when the account requires first-time MFA setup (EPIC-004 F2/E-004) */
  requiresMfaSetup?: boolean;
  /** True when anomaly detected — UI must force step-up TOTP re-verify (EPIC-004 F3/E-006) */
  requiresStepUp?: boolean;
  stepUpScope?: string;
  anomalies?: string[];
  /** Temporary user id for the 2FA step */
  userId?: string;
  /** Temp token to authorize the 2FA verify call */
  tempToken?: string;
  /** Present when login is complete (no 2FA or 2FA already skipped) */
  token?: string;
  refreshToken?: string;
  expiresIn?: number;
  email?: string;
  fullName?: string;
  role?: AdminRole;
  twoFactorEnabled?: boolean;
}

export interface Verify2FAResponse {
  success: boolean;
  message?: string;
  requiresStepUp?: boolean;
  stepUpScope?: string;
  anomalies?: string[];
  /** Novo tempToken emitido quando requiresStepUp=true (purpose admin_login_step_up). */
  tempToken?: string;
  token?: string;
  refreshToken?: string;
  expiresIn?: number;
  email?: string;
  fullName?: string;
  role?: AdminRole;
  usedBackupCode?: boolean;
}

export interface Setup2FAResponse {
  success: boolean;
  qrCode: string;
  secret: string;
  recoveryCodes: string[];
}

export interface Confirm2FAResponse {
  success: boolean;
  message?: string;
}

// ── Auth Functions ─────────────────────────────────────────

/**
 * Normaliza payload do gateway (snake_case) para o shape consumido pela UI.
 */
function normalizeLoginResponse(raw: any): LoginResponse {
  const success = Boolean(raw?.success);
  const requires2FA = Boolean(raw?.requires_2fa ?? raw?.requires2FA);
  return {
    success,
    message: raw?.message ?? undefined,
    requires2FA,
    requiresMfaSetup: Boolean(raw?.requires_mfa_setup ?? raw?.requiresMfaSetup),
    requiresStepUp: Boolean(raw?.requires_step_up ?? raw?.requiresStepUp),
    stepUpScope: raw?.step_up_scope ?? raw?.stepUpScope,
    anomalies: Array.isArray(raw?.anomalies) ? raw.anomalies : undefined,
    userId: raw?.user_id ?? raw?.userId,
    tempToken: raw?.temp_token ?? raw?.tempToken,
    // Gateway pode devolver access_token (snake), accessToken (camel) OU token (legado).
    // Banking-grade (cookie mode) retorna body scrubbed; fallback pra raw?.token.
    token: raw?.access_token ?? raw?.accessToken ?? raw?.token,
    refreshToken: raw?.refresh_token ?? raw?.refreshToken,
    expiresIn: raw?.expires_in ?? raw?.expiresIn,
    email: raw?.email,
    fullName: raw?.full_name ?? raw?.fullName,
    role: raw?.role,
    twoFactorEnabled: raw?.two_factor_enabled ?? raw?.twoFactorEnabled,
  };
}

/**
 * Login with email + password (admin).
 * If the admin has 2FA enabled, the backend returns requires2FA=true + tempToken.
 */
export async function loginWithEmail(params: {
  email: string;
  password: string;
  gps?: { lat: number; lon: number; accuracy: number };
  gpsBypassed?: boolean;
}): Promise<LoginResponse> {
  const { data } = await api.post('/auth/admin/login', {
    email: params.email.trim().toLowerCase(),
    password: params.password,
    ...(params.gps !== undefined
      ? {
          gps_lat: params.gps.lat,
          gps_lon: params.gps.lon,
          gps_accuracy: params.gps.accuracy,
          gps_bypassed_dev: params.gpsBypassed ?? false,
        }
      : {}),
  });
  return normalizeLoginResponse(data);
}

/**
 * Verify 2FA TOTP code (or backup code) during admin login flow.
 * Called after loginWithEmail returns requires2FA=true.
 */
export async function verify2FA(params: {
  userId: string;
  code: string;
  tempToken: string;
  rememberDevice?: boolean;
}): Promise<Verify2FAResponse> {
  const { data } = await api.post<any>('/auth/admin/2fa/verify', {
    user_id: params.userId,
    code: params.code,
    temp_token: params.tempToken,
    remember_device: params.rememberDevice ?? false,
  });
  return {
    success: Boolean(data?.success),
    message: data?.message,
    requiresStepUp: Boolean(data?.requires_step_up ?? data?.requiresStepUp),
    stepUpScope: data?.step_up_scope ?? data?.stepUpScope,
    anomalies: Array.isArray(data?.anomalies) ? data.anomalies : undefined,
    tempToken: data?.temp_token ?? data?.tempToken,
    token: data?.access_token ?? data?.accessToken ?? data?.token,
    refreshToken: data?.refresh_token ?? data?.refreshToken,
    expiresIn: data?.expires_in ?? data?.expiresIn,
    email: data?.email,
    fullName: data?.full_name ?? data?.fullName,
    role: data?.role,
    usedBackupCode: Boolean(data?.used_backup_code ?? data?.usedBackupCode),
  };
}

// ── Forgot Password (3-step OTP) ───────────────────────────

export interface ForgotRequestOTPResponse {
  success: boolean;
  maskedEmail?: string;
  message?: string;
}

export interface ForgotValidateOTPResponse {
  valid: boolean;
  resetToken?: string;
  message?: string;
}

export interface ForgotResetResponse {
  success: boolean;
  message?: string;
}

export interface ForgotPasswordGps {
  lat: number;
  lon: number;
  accuracy?: number | null;
}

export async function forgotPasswordRequestOTP(
  email: string,
  gps: ForgotPasswordGps,
): Promise<ForgotRequestOTPResponse> {
  const { data } = await api.post<any>('/auth/admin/forgot-password', {
    action: 'request_otp',
    email: email.trim().toLowerCase(),
    gps_lat: gps.lat,
    gps_lon: gps.lon,
    gps_accuracy: gps.accuracy ?? 0,
  });
  return {
    success: Boolean(data?.success),
    maskedEmail: data?.masked_email ?? data?.maskedEmail,
    message: data?.message,
  };
}

export async function forgotPasswordValidateOTP(params: {
  email: string;
  code: string;
  gps: ForgotPasswordGps;
}): Promise<ForgotValidateOTPResponse> {
  const { data } = await api.post<any>('/auth/admin/forgot-password', {
    action: 'validate_otp',
    email: params.email.trim().toLowerCase(),
    code: params.code,
    gps_lat: params.gps.lat,
    gps_lon: params.gps.lon,
    gps_accuracy: params.gps.accuracy ?? 0,
  });
  return {
    valid: Boolean(data?.valid),
    resetToken: data?.reset_token ?? data?.resetToken,
    message: data?.message,
  };
}

export async function forgotPasswordReset(params: {
  resetToken: string;
  newPassword: string;
  gps: ForgotPasswordGps;
}): Promise<ForgotResetResponse> {
  const { data } = await api.post<any>('/auth/admin/forgot-password', {
    action: 'reset_password',
    reset_token: params.resetToken,
    new_password: params.newPassword,
    gps_lat: params.gps.lat,
    gps_lon: params.gps.lon,
    gps_accuracy: params.gps.accuracy ?? 0,
  });
  return { success: Boolean(data?.success), message: data?.message };
}

// ── Change Password (authenticated) ────────────────────────

export async function changePassword(params: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  try {
    const { data } = await api.post<any>('/admin/password/change', {
      current_password: params.currentPassword,
      new_password: params.newPassword,
    });
    return { success: Boolean(data?.success), message: data?.message };
  } catch (err: any) {
    // Backend retorna 400 com mensagens descritivas (ex.: "Senha atual incorreta",
    // "Essa senha já foi usada recentemente"). Propagar a mensagem ao caller em vez
    // de lançar — handler usa res.message no toast.
    const message =
      err?.response?.data?.message ??
      (typeof err?.message === 'string' ? err.message : 'Erro ao atualizar senha');
    return { success: false, message };
  }
}

/**
 * Setup 2FA — generates TOTP secret + QR code + recovery codes.
 * The admin must then confirm with a valid code via confirm2FASetup.
 */
export async function setup2FA(adminId: string): Promise<Setup2FAResponse> {
  const { data } = await api.post<Setup2FAResponse>('/admin/2fa/setup', {
    admin_id: adminId,
  });
  return data;
}

/**
 * Confirm 2FA setup by entering the first valid TOTP code.
 * Gateway endpoint is POST /api/admin/2fa/enable (not "confirm").
 */
export async function confirm2FASetup(params: {
  adminId: string;
  code: string;
}): Promise<Confirm2FAResponse> {
  const { data } = await api.post<Confirm2FAResponse>('/admin/2fa/enable', {
    admin_id: params.adminId,
    code: params.code,
  });
  return data;
}

/**
 * Disable 2FA. Requires current TOTP code for verification.
 * Note: password verification is not supported by the gateway endpoint.
 */
export async function disable2FA(params: {
  adminId: string;
  code: string;
}): Promise<{ success: boolean; message?: string }> {
  const { data } = await api.post<{ success: boolean; message?: string }>('/admin/2fa/disable', {
    admin_id: params.adminId,
    code: params.code,
  });
  return data;
}

// ── Step-Up (EPIC-004 F3/E-004) ────────────────────────────

export interface StepUpResponse {
  success: boolean;
  stepUpToken?: string;
  expiresIn?: number;
  message?: string;
}

/**
 * Emite step-up token (TTL 5min, one-time, scope-bound) via POST /auth/admin/step-up.
 * Requer sessão admin ativa. Se `tempToken` fornecido, envia via Authorization header
 * (usado no fluxo anomaly pós-login quando ainda não há cookie de sessão).
 */
export async function requestStepUp(params: {
  totpCode: string;
  scope: string;
  tempToken?: string;
}): Promise<StepUpResponse> {
  const config = params.tempToken
    ? { headers: { Authorization: `Bearer ${params.tempToken}` } }
    : undefined;
  const { data } = await api.post<any>(
    '/auth/admin/step-up',
    { totp_code: params.totpCode, scope: params.scope },
    config,
  );
  return {
    success: Boolean(data?.success),
    stepUpToken: data?.step_up_token ?? data?.stepUpToken,
    expiresIn: Number(data?.expires_in ?? data?.expiresIn ?? 0),
    message: data?.message,
  };
}

// ── MFA First-Setup (EPIC-004 F2/E-004) ────────────────────

export interface FirstSetup2FAResponse {
  success: boolean;
  qrCodeUrl?: string;
  secret?: string;
  backupCodes?: string[];
  message?: string;
}

/**
 * Primeira configuração de 2FA — fluxo disparado por requires_mfa_setup=true no login.
 * Usa tempToken (purpose=admin_mfa_first_setup) pois admin ainda não tem sessão.
 * Endpoint: POST /api/auth/admin/2fa/first-setup
 */
export async function firstSetup2FA(tempToken: string): Promise<FirstSetup2FAResponse> {
  const { data } = await api.post<any>(
    '/auth/admin/2fa/first-setup',
    { temp_token: tempToken },
  );
  // Backend retorna { secret, qrCodeUrl, backupCodes } em sucesso (sem success flag),
  // ou { success: false, message } em falha de token. Inferir sucesso pela presença
  // dos campos obrigatórios.
  const qrCodeUrl = data?.qr_code_url ?? data?.qrCodeUrl;
  const secret = data?.secret;
  const backupCodes = data?.backup_codes ?? data?.backupCodes;
  const success = data?.success === false
    ? false
    : Boolean(qrCodeUrl && secret && Array.isArray(backupCodes));
  return {
    success,
    qrCodeUrl,
    secret,
    backupCodes,
    message: data?.message,
  };
}

/**
 * Confirma primeira configuração TOTP — valida código e ativa 2FA.
 * Endpoint: POST /api/auth/admin/2fa/first-enable
 */
export async function firstEnable2FA(params: {
  tempToken: string;
  code: string;
}): Promise<{ success: boolean; message?: string }> {
  const { data } = await api.post<any>(
    '/auth/admin/2fa/first-enable',
    { temp_token: params.tempToken, code: params.code },
  );
  return { success: Boolean(data?.success), message: data?.message };
}

/**
 * Refresh an expired access token.
 * Cookie mode (EPIC-004): no body — refresh cookie is sent automatically.
 * Legacy mode: requires refresh token in body.
 */
export async function refreshToken(refreshTkn?: string): Promise<{
  success: boolean;
  token?: string;
  refreshToken?: string;
  expiresIn?: number;
}> {
  if (USE_COOKIES) {
    const { data } = await api.post('/auth/admin/refresh', {});
    return data;
  }
  const { data } = await api.post('/auth/refresh', {
    refresh_token: refreshTkn ?? '',
  });
  return data;
}

/**
 * Revoke server-side session (cookie mode). Safe to call even in legacy mode —
 * endpoint just won't exist and we'll swallow the error.
 * EPIC-004 F1/E-002.
 */
export async function logoutServerSide(): Promise<void> {
  if (!USE_COOKIES) return;
  try {
    await api.post('/auth/admin/logout', {});
  } catch {
    // Server-side revocation is best-effort; client will clear state regardless.
  }
}

/**
 * Logout — clears local storage (legacy mode). No-op in cookie mode; the store
 * handles state reset and logoutServerSide revokes server cookies.
 */
export function logout(): void {
  if (USE_COOKIES) return;
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user_id');
}
